/**
 * In-Memory Cache Store
 * Fast cache for active session navigation and frequently accessed data
 * Automatically cleaned up on window unload
 */

class MemoryCache {
  constructor() {
    this.cache = new Map();
    this.timers = new Map();

    // Clean up on tab close
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.clear());
    }
  }

  /**
   * Set cache entry with TTL
   */
  set(key, value, ttl = 5 * 60 * 1000) {
    // Clear existing timer if any
    if (this.timers.has(key)) {
      clearTimeout(this.timers.get(key));
    }

    const entry = {
      data: value,
      timestamp: Date.now(),
      expiresAt: Date.now() + ttl,
    };

    this.cache.set(key, entry);

    // Set auto-expiration
    const timer = setTimeout(() => {
      this.cache.delete(key);
      this.timers.delete(key);
    }, ttl);

    this.timers.set(key, timer);
  }

  /**
   * Get cache entry
   */
  get(key) {
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    // Check if expired
    if (entry.expiresAt < Date.now()) {
      this.cache.delete(key);
      if (this.timers.has(key)) {
        clearTimeout(this.timers.get(key));
        this.timers.delete(key);
      }
      return null;
    }

    return entry.data;
  }

  /**
   * Check if key exists
   */
  has(key) {
    return this.get(key) !== null;
  }

  /**
   * Delete entry
   */
  delete(key) {
    this.cache.delete(key);
    if (this.timers.has(key)) {
      clearTimeout(this.timers.get(key));
      this.timers.delete(key);
    }
  }

  /**
   * Clear all cache
   */
  clear() {
    this.cache.forEach((_, key) => {
      if (this.timers.has(key)) {
        clearTimeout(this.timers.get(key));
      }
    });
    this.cache.clear();
    this.timers.clear();
  }

  /**
   * Get all entries
   */
  getAll() {
    const result = {};
    this.cache.forEach((entry, key) => {
      if (entry.expiresAt >= Date.now()) {
        result[key] = entry.data;
      }
    });
    return result;
  }

  /**
   * Get cache stats
   */
  getStats() {
    let totalSize = 0;
    let expiredCount = 0;
    const now = Date.now();

    this.cache.forEach((entry) => {
      totalSize += JSON.stringify(entry).length;
      if (entry.expiresAt < now) {
        expiredCount += 1;
      }
    });

    return {
      totalEntries: this.cache.size,
      totalSize,
      expiredEntries: expiredCount,
    };
  }
}

// Singleton instance
let memoryCache = null;

export const getMemoryCache = () => {
  if (!memoryCache) {
    memoryCache = new MemoryCache();
  }
  return memoryCache;
};

export default MemoryCache;
